import React, { useEffect, useRef } from 'react';
import { Box, Button, Checkbox, FormControlLabel, InputBase, Typography } from '@mui/material';
import { useUntypedTranslation } from '../../../../hooks/useUntypedTranslation';
import { focusRing, meta, mono, pressable } from '../../../../styles/visualLanguage';
import type {
  AdvisoryWorkspaceGap,
  AdvisoryWorkspaceSourcesProps,
  AdvisoryWorkspaceStatus,
} from './advisoryWorkspaceTypes';

export function WorkspaceStatus({ status }: { status: AdvisoryWorkspaceStatus }) {
  const { t } = useUntypedTranslation();
  const attention = ['slow', 'needsInput', 'partial', 'withGaps'].includes(status);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <Box
        component="span"
        aria-hidden
        sx={theme => ({
          width: 6,
          height: 6,
          borderRadius: '50%',
          flexShrink: 0,
          backgroundColor: attention
            ? theme.colors.reviewWarning
            : status === 'waiting' || status === 'notRequired'
            ? theme.colors.inkTertiary
            : theme.colors.lavenderDeep,
          ...(attention ? { boxShadow: `0 0 6px ${theme.colors.reviewWarning}66` } : {}),
        })}
      />
      {t('advisory:workspace.status.' + status)}
    </Box>
  );
}

function safeSourceUrl(value?: string): string | undefined {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password
      ? url.href
      : undefined;
  } catch {
    return undefined;
  }
}

/** URL and file inputs delegate to their owner; this component never uploads or starts research. */
export function AdvisoryWorkspaceSources(props: AdvisoryWorkspaceSourcesProps) {
  const { t } = useUntypedTranslation();
  const input = useRef<HTMLInputElement>(null);
  const triggers = useRef(new Map<string, HTMLButtonElement>());
  const previousEditor = useRef<string | null>(null);
  const editorKey = props.editor ? props.editor.companyId ?? '__engagement' : null;
  useEffect(() => {
    if (editorKey !== null && previousEditor.current !== editorKey) input.current?.focus();
    if (editorKey === null && previousEditor.current !== null)
      triggers.current.get(previousEditor.current)?.focus();
    previousEditor.current = editorKey;
  }, [editorKey]);
  const disabled = props.locked || props.busy || Boolean(props.editor?.busy);
  const editor = props.editor;
  const companyName = props.companies.find(company => company.id === editor?.companyId)?.name;
  const renderGap = (gap: AdvisoryWorkspaceGap) => {
    const profile = props.sources.some(
      source => source.id === gap.sourceId && source.kind === 'profile'
    );
    if (props.locked && gap.acknowledged)
      return (
        <Typography
          key={gap.sourceId}
          sx={[meta, theme => ({ mt: 1, color: theme.colors.reviewWarning })]}
        >
          {t(
            profile
              ? 'advisory:workspace.acceptedProfileGap'
              : 'advisory:workspace.acceptedSourceGap',
            {
              ...(profile ? { company: gap.label } : { source: gap.label }),
            }
          )}
        </Typography>
      );
    return (
      <FormControlLabel
        key={gap.sourceId}
        control={
          <Checkbox
            checked={gap.acknowledged}
            disabled={disabled}
            onChange={(_, checked) => props.onAcknowledgeGap(gap.sourceId, gap.generation, checked)}
          />
        }
        label={t(
          profile
            ? 'advisory:workspace.acknowledgeProfileGap'
            : 'advisory:workspace.acknowledgeSourceGap',
          {
            ...(profile ? { company: gap.label } : { source: gap.label }),
          }
        )}
        sx={{ mt: 1, '& .MuiFormControlLabel-label': { fontSize: 14 } }}
      />
    );
  };

  return (
    <Box component="section" aria-labelledby="workspace-company-heading" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Typography component="h2" id="workspace-company-heading" variant="h6">
          {t('advisory:workspace.companyTitle')}
        </Typography>
        {props.companies.length > 0 && (
          <Typography sx={[meta]}>
            {t('advisory:workspace.companyCount', {
              ready: props.companies.filter(
                company => company.status === 'ready' || company.status === 'completed'
              ).length,
              total: props.companies.length,
            })}
          </Typography>
        )}
      </Box>
      {!props.companies.length && (
        <Typography sx={[meta, { mt: 1 }]}>{t('advisory:workspace.noCompanies')}</Typography>
      )}
      {props.companies.map(company => (
        <Box
          key={company.id}
          sx={theme => ({ py: 2, borderBottom: `1px solid ${theme.visualPrimitives.border}` })}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
            }}
          >
            <Box>
              <Typography sx={[mono, { fontSize: 19 }]}>{company.name}</Typography>
              <Typography sx={[meta, { mt: 0.5 }]}>
                <WorkspaceStatus status={company.status} />
              </Typography>
            </Box>
            {!props.locked && (
              <Button
                type="button"
                ref={node => {
                  if (node) triggers.current.set(company.id, node);
                }}
                disabled={disabled}
                onClick={() => props.onOpenSources(company.id)}
                aria-expanded={editor?.companyId === company.id}
                aria-label={t('advisory:workspace.addCompanySources', { company: company.name })}
                sx={[pressable, focusRing, { flexShrink: 0 }]}
              >
                {t('advisory:workspace.addSources')}
              </Button>
            )}
          </Box>
          {company.hintKey && (
            <Typography sx={[meta, theme => ({ mt: 1, color: theme.colors.reviewWarning })]}>
              {t(company.hintKey)}
            </Typography>
          )}
          {!company.hintKey && company.status === 'slow' && (
            <Typography sx={[meta, theme => ({ mt: 1, color: theme.colors.reviewWarning })]}>
              {t('advisory:workspace.slowHint')}
            </Typography>
          )}
          {!company.hintKey && company.status === 'needsInput' && (
            <Typography sx={[meta, theme => ({ mt: 1, color: theme.colors.reviewWarning })]}>
              {t('advisory:workspace.inputHint')}
            </Typography>
          )}
          {props.gaps.filter(gap => gap.companyId === company.id).map(renderGap)}
        </Box>
      ))}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          mt: 3,
        }}
      >
        <Typography component="h3" variant="subtitle1">
          {t('advisory:workspace.sourcesTitle')}
        </Typography>
        {!props.locked && (
          <Button
            type="button"
            ref={node => {
              if (node) triggers.current.set('__engagement', node);
            }}
            disabled={disabled}
            onClick={() => props.onOpenSources(null)}
            aria-expanded={Boolean(editor && editor.companyId === null)}
            sx={[pressable, focusRing]}
          >
            {t('advisory:workspace.addSources')}
          </Button>
        )}
      </Box>
      {!props.sources.length && (
        <Typography sx={[meta]}>{t('advisory:workspace.noSources')}</Typography>
      )}
      <Box
        component="ul"
        aria-label={t('advisory:workspace.sourcesTitle')}
        sx={{ listStyle: 'none', m: 0, p: 0 }}
      >
        {props.sources.map(source => {
          const href = safeSourceUrl(source.url);
          const associatedCompany = props.companies.find(
            company => company.id === source.companyId
          )?.name;
          return (
            <Box
              component="li"
              key={source.id}
              sx={theme => ({
                py: 1.5,
                borderBottom: `1px solid ${theme.visualPrimitives.border}`,
                overflowWrap: 'anywhere',
              })}
            >
              {href ? (
                <Box
                  component="a"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={[focusRing, theme => ({ color: theme.colors.lavenderDeep })]}
                >
                  {source.name}
                </Box>
              ) : (
                <Typography>{source.name}</Typography>
              )}
              <Typography
                sx={[
                  meta,
                  source.status === 'failed' || source.attention
                    ? theme => ({ color: theme.colors.reviewWarning })
                    : {},
                ]}
              >
                {t('advisory:workspace.sourceKind.' + source.kind)}
                {' · '}
                {t(source.statusKey ?? 'advisory:workspace.sourceStatus.' + source.status)}
                {associatedCompany ? ` · ${associatedCompany}` : ''}
              </Typography>
              {source.needsFile && !props.locked && props.onResumeUpload && (
                <Box sx={{ mt: 1 }}>
                  <Typography
                    component="label"
                    htmlFor={'workspace-resume-' + source.id}
                    sx={[meta]}
                  >
                    {t('advisory:workspace.live.resumeFile', { name: source.name })}
                  </Typography>
                  <input
                    disabled={disabled}
                    id={'workspace-resume-' + source.id}
                    type="file"
                    style={{ maxWidth: '100%' }}
                    onChange={event => {
                      const file = event.target.files?.[0];
                      if (file) props.onResumeUpload?.(source.id, file);
                      event.target.value = '';
                    }}
                  />
                </Box>
              )}
              {source.status === 'failed' && source.retryable && !props.locked && (
                <Button
                  disabled={disabled}
                  onClick={() => props.onRetrySource(source.id)}
                  sx={[pressable, focusRing]}
                >
                  {t(source.retryLabelKey ?? 'advisory:workspace.retrySource')}
                </Button>
              )}
            </Box>
          );
        })}
      </Box>

      {props.gaps.filter(gap => gap.companyId === null).map(renderGap)}

      {editor && !props.locked && (
        <Box
          component="form"
          aria-label={t('advisory:workspace.sourceForm')}
          onSubmit={event => {
            event.preventDefault();
            if (!disabled && editor.url.trim()) props.onAddUrl();
          }}
          onKeyDown={event => {
            if (event.key === 'Escape' && !disabled) {
              event.preventDefault();
              props.onCloseSources();
            }
          }}
          sx={theme => ({
            mt: 2,
            p: { xs: 2, sm: 2.5 },
            border: `1px solid ${theme.visualPrimitives.border}`,
            borderRadius: 2,
            backgroundColor: theme.palette.background.default,
          })}
        >
          <Typography sx={[meta, { mb: 1.5 }]}>
            {companyName
              ? t('advisory:workspace.sourcesFor', { company: companyName })
              : t('advisory:workspace.engagementSources')}
          </Typography>
          <Typography component="label" htmlFor="workspace-source-url" sx={[meta]}>
            {t('advisory:workspace.urlLabel')}
          </Typography>
          <InputBase
            id="workspace-source-url"
            inputRef={input}
            type="url"
            fullWidth
            value={editor.url}
            disabled={disabled}
            onChange={event => props.onUrlChange(event.target.value)}
            placeholder={t('advisory:workspace.urlPlaceholder')}
            inputProps={{
              'aria-describedby': editor.errorKey
                ? 'workspace-source-error'
                : 'workspace-source-hint',
            }}
            sx={theme => ({
              my: 1,
              px: 1.5,
              py: 1,
              border: `1px solid ${theme.visualPrimitives.border}`,
              borderRadius: 1,
              fontSize: 16,
            })}
          />
          <Typography id="workspace-source-hint" sx={[meta]}>
            {t('advisory:workspace.urlHint')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
            <Button
              type="submit"
              disabled={disabled || !editor.url.trim()}
              sx={[pressable, focusRing]}
            >
              {t('advisory:workspace.addUrl')}
            </Button>
            <Button
              type="button"
              disabled={disabled}
              onClick={props.onCloseSources}
              sx={[pressable, focusRing]}
            >
              {t('advisory:workspace.cancel')}
            </Button>
          </Box>
          <Typography
            component="label"
            htmlFor="workspace-source-files"
            sx={[meta, { display: 'block', mt: 2, mb: 1 }]}
          >
            {t('advisory:workspace.fileLabel')}
          </Typography>
          <input
            id="workspace-source-files"
            type="file"
            multiple
            disabled={disabled}
            style={{ maxWidth: '100%' }}
            onChange={event => {
              const files = Array.from(event.target.files ?? []);
              if (files.length && !disabled) props.onAddFiles(files, editor.companyId);
              event.target.value = '';
            }}
          />
          {editor.errorKey && (
            <Typography
              role="alert"
              id="workspace-source-error"
              sx={[meta, theme => ({ mt: 1, color: theme.colors.reviewWarning })]}
            >
              {t(editor.errorKey)}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
